import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Descriptions, message, Card, Spin, Space, Popconfirm,
  Modal, Form, Input, Select, Table, Tabs, Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined, EditOutlined, SaveOutlined, DeleteOutlined, HistoryOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import { skillsApi, dictionariesApi } from '../services/api-services';
import { useStreamGeneration } from '../hooks/useStreamGeneration';
import MdEditor from 'react-markdown-editor-lite';
import MarkdownIt from 'markdown-it';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'react-markdown-editor-lite/lib/index.css';
import '../styles/markdown.css';

const mdParser = new MarkdownIt();

const SkillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { generate: streamGenerate } = useStreamGeneration();
  const [skill, setSkill] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [versionModalVisible, setVersionModalVisible] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [editForm] = Form.useForm();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'super_admin' || user.role === 'admin';
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isFullscreen = !!document.querySelector('.rc-md-editor.full');
      document.documentElement.classList.toggle('md-fullscreen', isFullscreen);
    });
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      document.documentElement.classList.remove('md-fullscreen');
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await skillsApi.get(Number(id));
      setSkill(res);
    } catch {
      message.error('获取 Skill 详情失败');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (id) fetchData();
    dictionariesApi.getByCode('resource_tag').then((dict: any) => {
      if (dict?.items) setTagOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    }).catch(() => {});
  }, [id, fetchData]);

  const handleSaveContent = async () => {
    try {
      await skillsApi.update(Number(id), { content: editedContent });
      message.success('内容保存成功');
      setIsEditing(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '保存失败');
    }
  };

  const handleGenerate = () => {
    console.log('[SkillDetail] handleGenerate called');
    // 退出编辑模式
    setIsEditing(false);
    let collectedContent = '';
    
    streamGenerate({
      action: 'create_task',
      payload: { 
        key: 'generate_skill',
        id: Number(id),
        resourceName: `${skill?.title || '技能'} - 内容生成`,
      },
      navigationUrl: `/skills/${id}`,
      onChunk: (chunk: string) => {
        collectedContent += chunk;
      },
      onDone: async () => {
        // 提取描述内容（第2行的"描述："后面的内容）
        const descMatch = collectedContent.match(/^#[^\n]*\n描述[：:]\s*([^\n]+)/);
        const descContent = descMatch ? descMatch[1].trim() : '';
        
        // 自动保存
        try {
          await skillsApi.update(Number(id), { content: collectedContent });
          // 如果提取到描述内容，同时更新 description
          if (descContent && skill) {
            await skillsApi.update(Number(id), { description: descContent });
          }
          fetchData();
        } catch (e: any) {
          message.error(e.response?.data?.detail || '保存失败');
        }
      }
    });
  };

  const openEditModal = () => {
    editForm.setFieldsValue({
      identifier: skill.identifier,
      title: skill.title,
      description: skill.description || '',
      tags: skill.tags || [],
    });
    setEditModalVisible(true);
  };

  const handleEditSave = async (values: any) => {
    try {
      await skillsApi.update(Number(id), values);
      message.success('更新成功');
      setEditModalVisible(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '更新失败');
    }
  };

  const handleDelete = async () => {
    try {
      await skillsApi.delete(Number(id));
      message.success('删除成功');
      navigate('/skills');
    } catch {
      message.error('删除失败');
    }
  };

  const handleViewVersions = async () => {
    try {
      const res: any = await skillsApi.versions(Number(id));
      setVersions(res);
      setVersionModalVisible(true);
    } catch {
      message.error('获取版本历史失败');
    }
  };

  const handleRestore = async (version: number) => {
    try {
      await skillsApi.restore(Number(id), version);
      message.success('恢复成功');
      setVersionModalVisible(false);
      fetchData();
    } catch (e: any) {
      message.error(e.response?.data?.detail || '恢复失败');
    }
  };

  if (loading && !skill) {
    return (
      <div className="apple-page" style={{ textAlign: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!skill) {
    return (
      <div className="apple-page">
        <div className="apple-empty">
          <p className="apple-empty-title">未找到 Skill</p>
          <Button type="primary" onClick={() => navigate('/skills')}>返回列表</Button>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: '基础信息',
      children: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="标识">{skill.identifier}</Descriptions.Item>
          <Descriptions.Item label="名称">{skill.title}</Descriptions.Item>
          <Descriptions.Item label="描述">{skill.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="版本">
            <span className="apple-tag">v{skill.version}</span>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{skill.created_at ? new Date(skill.created_at).toLocaleString() : '-'}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{skill.updated_at ? new Date(skill.updated_at).toLocaleString() : '-'}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'content',
      label: '内容',
      children: (
        <Card
          title="内容"
          styles={{ body: { padding: 0, overflow: 'hidden', borderRadius: '0 0 8px 8px' } }}
          extra={
            isEditing ? (
              <Space>
                <Button icon={<RobotOutlined />} onClick={handleGenerate}>AI 生成</Button>
                <Button onClick={() => setIsEditing(false)}>取消</Button>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveContent}>保存</Button>
              </Space>
            ) : (
              <Space>
                <Button type="text" icon={<EditOutlined />} onClick={() => { setEditedContent(skill.content || ''); setIsEditing(true); }} />
              </Space>
            )
          }
        >
          {isEditing ? (
            <MdEditor
              value={editedContent}
              style={{ height: '500px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setEditedContent(text)}
              config={{
                view: { menu: true, md: true, html: true },
                canView: { menu: true, md: true, html: true, fullScreen: true, hideMenu: true },
              }}
            />
          ) : (
            <div className="markdown-preview">
              {skill.content ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{skill.content}</ReactMarkdown> : <p style={{ color: 'var(--apple-text-secondary)', padding: 24 }}>暂无内容</p>}
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div className="apple-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/skills')} />
          <h1 className="apple-page-title" style={{ marginBottom: 0 }}>{skill.title}</h1>
        </Space>
        <Space>
          <Button icon={<EditOutlined />} onClick={openEditModal}>编辑</Button>
          <Button icon={<HistoryOutlined />} onClick={handleViewVersions}>版本历史</Button>
          {(isAdmin) && (
            <Popconfirm title="确认删除" description="确定要删除此 Skill 吗？" onConfirm={handleDelete} okText="确定" cancelText="取消">
              <Button danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <Tabs items={tabItems} />

      <Modal
        className="apple-modal"
        title="编辑 Skill"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => editForm.submit()}
        destroyOnHidden
      >
        <Form className="apple-form" form={editForm} layout="vertical" onFinish={handleEditSave}>
          <Form.Item name="identifier" label="标识" rules={[{ required: true }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和连字符' }, { max: 64, message: '最长64字符' }]}><Input /></Form.Item>
          <Form.Item name="title" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="描述"><Input.TextArea rows={6} style={{ minHeight: 160 }} /></Form.Item>
          <Form.Item name="tags" label="标签">
            <Select mode="multiple" placeholder="选择标签" options={tagOptions} allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        className="apple-modal"
        title="版本历史"
        open={versionModalVisible}
        onCancel={() => setVersionModalVisible(false)}
        footer={null}
        width={700}
      >
        <div className="apple-table-wrapper">
          <Table dataSource={versions} rowKey="id" pagination={false}>
            <Table.Column title="版本" dataIndex="version" key="version" render={(v: number) => <span className="apple-tag">v{v}</span>} />
            <Table.Column title="创建时间" dataIndex="created_at" key="created_at" render={(v: string) => v ? new Date(v).toLocaleString() : '-'} />
            <Table.Column title="操作" render={(_: any, record: any) => (
              <Button type="link" size="small" onClick={() => handleRestore(record.version)}>恢复到此版本</Button>
            )} />
          </Table>
        </div>
      </Modal>
    </div>
  );
};

export default SkillDetail;
